import { useState } from 'react'
import { waitlistService } from '../lib/supabase'

export interface UseWaitlistReturn {
  isLoading: boolean
  isSubmitted: boolean
  error: string | null
  message: string | null
  submitEmail: (email: string, source?: string) => Promise<void>
  checkEmail: (email: string) => Promise<boolean>
  reset: () => void
}

export function useWaitlist(): UseWaitlistReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const submitEmail = async (email: string, source: string = 'website') => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    setError(null)
    setMessage(null)

    try {
      // Check if email already exists
      const emailExists = await waitlistService.checkEmailExists(email)
      
      if (emailExists) {
        setIsSubmitted(true)
        setMessage("You're already on our waitlist! We'll keep you updated on our launch.")
        console.log('Email already on waitlist:', { email, source })
        return
      }

      await waitlistService.addToWaitlist(email, source, {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct'
      })
      
      setIsSubmitted(true)
      setMessage("Welcome to the revolution! You're now on the UNDRESSING waitlist.")
      
      // Track successful signup (you can add analytics here)
      console.log('Waitlist signup successful:', { email, source })
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to join waitlist'
      setError(errorMessage)
      console.error('Waitlist signup error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const checkEmail = async (email: string): Promise<boolean> => {
    try {
      const exists = await waitlistService.checkEmailExists(email)
      return !!exists
    } catch (err) {
      console.error('Error checking email:', err)
      return false
    }
  }

  const reset = () => {
    setIsLoading(false)
    setIsSubmitted(false)
    setError(null)
    setMessage(null)
  }

  return {
    isLoading,
    isSubmitted,
    error,
    message,
    submitEmail,
    checkEmail,
    reset
  }
}